import { DirectiveOptions, DirectiveFunction } from 'vue'
import { DirectiveBinding } from 'vue/types/options'
import { VNode } from 'vue/types/umd'

// Used to pass in as default to say to skip the v-track
export const DO_NOT_TRACK = 'DO_NOT_TRACK'

export const camelCaseTitle = (testTitle?: string) => {
  if (!testTitle) return 'unnamed'
  return testTitle.split(' ').filter(word => word).map((word, index) => {
    if (index > 0) {
      return word[0].toUpperCase() + word.slice(1)
    } else {
      return word.toLowerCase()
    }
  }).join('')
}

const trackingMap = {
  sliceInsight: 'sliceInsightsClick',
  mainInsight: 'mainInsightsClick',
  followUp: 'followUp',
  share: 'share',
  detailsAddProject: 'addProject',
  detailsAddTag: 'addTag',
  detailsAddFollower: 'addFollower',
  deleteLink: 'deleteLink',
  generateLink: 'generateLink',
  shareClickLink: 'shareClickLink',
  viewAll: 'viewAll',
  findingsInput: 'findingsInput',
  questionVisibleToggle: 'question__visibleToggle',
  chartExportPng: 'chart__exportPng',
  chartExportSVG: 'chart__exportSVG',
  chartCopyUrl: 'chart__copyUrl',
  questionConfigEdit: 'question__config__edit',
  questionOpenEndSearch: 'question__openEnd__search',
  questionConfigRevert: 'question__config__revert',
  questionOpenEndManageClick: 'question__openEnd__manageClick',
  questionTitleInput: 'question__title__input',
  questionTitleSave: 'question__title__save',
  questionTitleCancel: 'question__title__cancel',
  manageRespondentResponseToggle: 'manageRespondent__response__toggle',
  manageRespondentIncludeResponse: 'manageRespondent__includeResponse',
  manageRespondentRespondentClick: 'manageRespondent__respondentClick',
  manageRespondentCancel: 'manageRespondent__cancel',
  manageRespondentSave: 'manageRespondent__save',
  manageRespondentPreviousQuestion: 'manageRespondent__previousQuestion',
  manageRespondentNextQuestion: 'manageRespondent__nextQuestion',
  manageRespondentIncludeRespondent: 'manageRespondent__includeRespondent',
  exportCsv: 'exportCsv',
  exportAll: 'exportAll',
  exportPdf: 'exportPdf',
  exportDocx: 'exportDocx',
  exportScreenerCsv: 'exportScreenerCsv',
  tabs: 'tabs',
  testViewBySlice: 'view__by__slice',
  colorConfigChange: 'color__config__change',
  colorConfigRevert: 'color__config__revert',
  addTest: 'addTest',
  login: 'login'
}

type TrackingMapKey = keyof typeof trackingMap;

export const TrackKeys = Object.keys(trackingMap).reduce((a: Record<string, string>, k) => ({ ...a, [k]: k }), {}) as Record<TrackingMapKey, TrackingMapKey>

const getTrackingId = (key?: string): string | undefined => {
  if (!key) {
    console.error('Could not find set binding for undefined value')
    return
  }
  const [trackId, dynamicId] = key.split(/-(.+)/)
  const trackKey = trackingMap[trackId as TrackingMapKey]
  if (!trackKey) {
    console.warn('Tracking value should be added to trackingMap')
    return prefixTrackKey(key)
  }
  return prefixTrackKey(trackKey, dynamicId)
}

const prefixTrackKey = (trackKey: string, dynamicId?: string) => {
  return dynamicId ? `test__${trackKey}-${dynamicId}` : `test__${trackKey}`
}

const bindTrackingId = (el: HTMLElement, binding: DirectiveBinding) => {
  // We have some dynamic v-track directives, passing this string in is a way to disable it
  if (binding.value === DO_NOT_TRACK) return
  const trackingId = getTrackingId(binding.value)

  if (trackingId) {
    setAttribute(el, 'data-track-id', trackingId)
    setAttribute(el, 'id', trackingId)
  }
}

const setAttribute = (element: HTMLElement, attribute: string, value: string) => {
  if (element.getAttribute(attribute)) {
    console.error(`Element already has attribute: ${attribute}`)
  } else {
    element.setAttribute(attribute, value)
  }
}

const bind: DirectiveFunction = (el: HTMLElement, binding: DirectiveBinding, vnode: VNode) => {
  bindTrackingId(el, binding)
}

const trackingDirective: DirectiveOptions = {
  bind
}

export {
  trackingDirective
}
